import { useMemo } from "react";
import { useDispatch } from "react-redux";
import {
    createColumnHelper,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    TableState,
    useReactTable
} from "@tanstack/react-table";
import { resources } from "@constants/hardcoded-strings";
import { TableSettingsTableTypes } from "@enums/table-settings-table-types";
import { useTableFilters } from "@hooks/use-table-filters";
import { FormattedIdentityProviderConfiguration } from "@idw-types/data/identity-providers";
import { sortStrings } from "@utils/string-util";
import { PaginationContainer } from "@containers/common/pagination-container/pagination-container";
import { IdwTable } from "@containers/common/idw-table/idw-table";
import { IdwTableContainer } from "@containers/common/idw-table-container/idw-table-container";
import { IdentityProvidersTableCell } from "../identity-providers-table-cell/identity-providers-table-cell";
import styles from "./identity-providers-table-container.less";
import { handleSetColumnFiltersChange, handleSetPaginationChange, handleSetSortingChange } from "@utils/react-table/react-table-util";

type IdentityProvidersTableContainerProps = {
    identityProviderConfigurations: Array<FormattedIdentityProviderConfiguration>;
    tableState: TableState;
    isPageHeight: boolean;
};

type IdentityProvidersTableDataRow = FormattedIdentityProviderConfiguration & {
    categoryValue: string;
    companyName: string;
    changeRequestStatusString: string;
};

export const IdentityProvidersTableContainer = (props: IdentityProvidersTableContainerProps) => {
    const { identityProviderConfigurations, tableState, isPageHeight } = props;
    const dispatch = useDispatch();

    const identityProviderTableData: Array<IdentityProvidersTableDataRow> = useMemo(() => {
        return identityProviderConfigurations.map(config => ({
            ...config,
            categoryValue: config.category?.value || resources.common.notFound,
            companyName: config.company?.name || resources.common.notFound,
            changeRequestStatusString: config.changeRequestStatus || resources.common.notFound
        }));
    }, [identityProviderConfigurations]);

    const columnHelper = createColumnHelper<IdentityProvidersTableDataRow>();

    const columns = useMemo(() => ([
        columnHelper.accessor("name", {
            header: resources.tables.identityProviders.columns.idpName.header,
            cell: (props) => <IdentityProvidersTableCell identityProviderConfiguration={props.row.original} />,
            filterFn: "fuzzyText",
            sortingFn: (a, b) => sortStrings(a.original.name, b.original.name)
        }),
        columnHelper.accessor("categoryValue", {
            header: resources.tables.identityProviders.columns.category.header,
            filterFn: "fuzzyText"
        }),
        columnHelper.accessor("companyName", {
            header: resources.tables.identityProviders.columns.organization.header,
            filterFn: "fuzzyText",
            sortingFn: (a, b) => sortStrings(a.original.companyName, b.original.companyName)
        }),
        columnHelper.accessor("changeRequestStatusString", {
            header: resources.tables.identityProviders.columns.requestStatus.header,
            filterFn: "fuzzyText"
        })
    ]), [identityProviderTableData]);

    const { filterTypes } = useTableFilters();

    const useTableResult = useReactTable({
        columns,
        data: identityProviderTableData,
        state: tableState,
        filterFns: filterTypes,
        sortDescFirst: false,
        autoResetPageIndex: true,
        onSortingChange: (sortingOrUpdateFn) => handleSetSortingChange(sortingOrUpdateFn, TableSettingsTableTypes.IDENTITY_PROVIDERS, tableState.sorting, dispatch),
        onColumnFiltersChange: (filtersOrUpdateFn) => handleSetColumnFiltersChange(filtersOrUpdateFn, TableSettingsTableTypes.IDENTITY_PROVIDERS, tableState.columnFilters, dispatch),
        onPaginationChange: (paginationOrUpdateFn) => handleSetPaginationChange(paginationOrUpdateFn, TableSettingsTableTypes.IDENTITY_PROVIDERS, tableState.pagination, dispatch),
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel()
    });

    return (
        <IdwTableContainer>
            <IdwTable
                id="identity-providers"
                useTableResult={useTableResult}
                className={styles.identityProvidersTable}
                headerColumnKeyPrefix={"identity-providers"}
            />
            <PaginationContainer
                idPrefix="identity-providers"
                useTableResult={useTableResult}
                isPageHeight={isPageHeight}
            />
        </IdwTableContainer>
    );
};